import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Image,
  InputGroup,
  ListGroup,
  Modal,
  OverlayTrigger,
  ProgressBar,
  Row,
  Tooltip,
  Toast,
  ToastContainer,
} from "react-bootstrap";

import moment from "moment";

// ---- Helpers ----
const currency = (n) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(n);

const sampleContacts = [
  { id: 1, name: "Alex Kim", phone: "+15552220111", email: "alex@example.com" },
  {
    id: 2,
    name: "Jordan Lee",
    phone: "+15552220112",
    email: "jordan@example.com",
  },
  { id: 3, name: "Sam Patel", phone: "+15552220113", email: "sam@example.com" },
  {
    id: 4,
    name: "Taylor Brooks",
    phone: "+15552220114",
    email: "taylor@example.com",
  },
];

const avatars = [
  "https://i.pravatar.cc/64?img=5",
  "https://i.pravatar.cc/64?img=15",
  "https://i.pravatar.cc/64?img=25",
  "https://i.pravatar.cc/64?img=35",
  "https://i.pravatar.cc/64?img=45",
  "https://i.pravatar.cc/64?img=55",
];

export default function GroupManageWrapper({
  data,
  eventDate = "Fri, Oct 10 • 7:30 PM",
  venue = "Grand Arena",
  inviteLink = "https://example.com/i/TEAM-ABCD",
  initialFriends = [
    { id: "f1", name: "Alex Kim", status: "Going" },
    { id: "f2", name: "Jordan Lee", status: "Invited" },
  ],
}) {
  const [friends, setFriends] = useState(initialFriends);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const linkRef = useRef(null);

  const goingCount = friends.filter((f) => f.status === "Going").length + 1; // +1 for the host
  const event = data?.event?.data?.attributes;

  const filteredContacts = useMemo(() => {
    const q = search.toLowerCase();
    return sampleContacts.filter((c) => c.name.toLowerCase().includes(q));
  }, [search]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setToastMsg("Invite link copied");
      setShowToast(true);
    } catch (e) {
      // fallback
      const el = document.createElement("textarea");
      el.value = inviteLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setToastMsg("Invite link copied");
      setShowToast(true);
    }
  };

  const webShare = async () => {
    const shareData = {
      title: `Join me at ${event?.name}`,
      text: `We're going to ${event?.name} at ${venue} on ${eventDate}. Grab your spot in our group and save!`,
      url: inviteLink,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        setToastMsg("Share not supported. Use copy/email/SMS instead.");
        setShowToast(true);
      }
    } catch (e) {
      setToastMsg("Share canceled");
      setShowToast(true);
    }
  };

  const mailto = () => {
    const subject = encodeURIComponent(`Join me at ${event?.name}`);
    const body = encodeURIComponent(
      `Hey! I'm getting a group together for ${event?.name} at ${venue} on ${eventDate}. Use this link to join: ${inviteLink}`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const sms = () => {
    const body = encodeURIComponent(
      `Join me at ${event?.name}! Grab your spot: ${inviteLink}`,
    );
    // sms: scheme varies by platform; use generic
    window.location.href = `sms:?&body=${body}`;
  };

  const whatsapp = () => {
    const text = encodeURIComponent(`Join me at ${event?.name}! ${inviteLink}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleInviteSelected = () => {
    const added = selectedContacts.map((c) => ({
      id: `c-${c.id}`,
      name: c.name,
      status: "Invited",
    }));
    setFriends((prev) => {
      const names = new Set(prev.map((p) => p.name));
      const uniqueAdds = added.filter((a) => !names.has(a.name));
      return [...prev, ...uniqueAdds];
    });
    setSelectedContacts([]);
    setShowAdd(false);
    setToastMsg("Invites sent");
    setShowToast(true);
  };

  const markGoing = (id) => {
    setFriends((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: "Going" } : f)),
    );
  };

  const removeFriend = (id) =>
    setFriends((prev) => prev.filter((f) => f.id !== id));

  const openSeatMap = () => {
    console.log("Opening Seatmap ...");
  };

  useEffect(() => {
    console.log(data);
  }, []);

  return (
    <Row className="mt-5">
      <Col lg={8}>
        <Card className="shadow-sm border-0">
          <Card.Body>
            <Row className="align-items-center gy-3">
              {/* <Col xs="auto">
                <div className="d-flex">
                  {[0, 1, 2, 3].map((i) => (
                    <Image key={i} roundedCircle width={48} height={48} className="me-1 border" src={avatars[i]} alt="avatar" />
                  ))}
                  <div className="rounded-circle bg-light border d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                    <span className="fw-semibold">+{Math.max(0, friends.length - 3)}</span>
                  </div>
                </div>
              </Col> */}
              <Col>
                <h5 className="mb-1">
                  Invite others to{" "}
                  <span className="text-primary">{event?.name}</span>
                </h5>
                <div className="text-muted small">
                  {eventDate} • {venue}
                </div>
              </Col>
              <Col xs="auto">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>Share with your favorite app</Tooltip>}
                >
                  <Button variant="primary" onClick={webShare} className="me-2">
                    Share
                  </Button>
                </OverlayTrigger>
                <Dropdown align="end">
                  <Dropdown.Toggle variant="outline-secondary">
                    More
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={mailto}>
                      Share via Email
                    </Dropdown.Item>
                    <Dropdown.Item onClick={sms}>Share via SMS</Dropdown.Item>
                    <Dropdown.Item onClick={whatsapp}>
                      Share via WhatsApp
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>

            <Row className="mt-4 g-3">
              <Col md={7}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="fw-semibold">Split with Friends</div>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => setShowAdd(true)}
                      >
                        Add friends
                      </Button>
                    </div>
                    <ListGroup variant="flush">
                      {friends.map((f, idx) => (
                        <ListGroup.Item
                          key={f.id}
                          className="px-0 d-flex align-items-center"
                        >
                          <Image
                            roundedCircle
                            width={40}
                            height={40}
                            className="me-2"
                            src={avatars[idx % avatars.length]}
                            alt={f.name}
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{f.name}</div>
                            <div className="small text-muted">
                              {f.status === "Going" ? "Confirmed" : "Invited"}
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {f.status !== "Going" && (
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => markGoing(f.id)}
                              >
                                Mark going
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => removeFriend(f.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                      {friends.length === 0 && (
                        <div className="text-muted small">
                          No invites yet—be the first to invite someone!
                        </div>
                      )}
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={5}>
                <Card className="border-0 h-100">
                  <Card.Body>
                    <div className="fw-semibold">FAQ</div>
                    <div className="small text-muted">
                      <div className="mt-2">
                        <span className="fw-semibold">
                          How do refunds work?
                        </span>{" "}
                        If someone cancels before the deadline, their spot opens
                        for another friend.
                      </div>
                      <div className="mt-2">
                        <span className="fw-semibold">Can I change seats?</span>{" "}
                        You can upgrade or move within the group if inventory is
                        available.
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={4}>
        <Card className="shadow-sm border-0">
          <Card.Body>
            <div className="d-flex align-items-center mb-3">
              <Image
                rounded
                width={64}
                height={64}
                className="me-3 border"
                src="https://images.unsplash.com/photo-1517649763962-0c623066013b?q=80&w=300&auto=format&fit=crop"
                alt="event"
              />
              <div>
                <div className="fw-semibold">{event?.name}</div>
                <div className="text-muted small">{eventDate}</div>
                <div className="text-muted small">{venue}</div>
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <div className="text-muted small">Amount Due</div>
              <div className="fw-semibold text-success">
                {currency(data?.depositAmount)}
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div className="text-muted small">Due Date</div>
              <div className="fw-semibold text-dark">
                {moment(data?.dueBy).format("MM/DD/YYYY hh:mm A")}
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div className="text-muted small">Seats Allocated</div>
              <div className="fw-semibold text-dark">
                {data?.maxSeatsAllowed}
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div className="text-muted small">Seats Remaining</div>
              <div className="fw-semibold text-dark">
                {data?.maxSeatsAllowed - Number(data?.tickets?.data?.length)}
              </div>
            </div>
            <hr />
            {/* <div className="d-flex justify-content-between mb-3">
              <div className="fw-semibold">Est. price if goal met</div>
              <div className="fw-semibold">{currency(basePrice * (1 - groupPerk.value))}</div>
            </div> */}
            <Button variant="primary" className="w-100">
              Select Seats
            </Button>
          </Card.Body>
        </Card>
      </Col>

      {/* Add friends modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add friends</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Control
                placeholder="Search contacts"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={6} className="text-md-end mt-2 mt-md-0">
              <OverlayTrigger overlay={<Tooltip>Coming soon</Tooltip>}>
                <span>
                  <Button variant="outline-secondary" disabled className="me-2">
                    Import from phone
                  </Button>
                </span>
              </OverlayTrigger>
              <OverlayTrigger overlay={<Tooltip>Coming soon</Tooltip>}>
                <span>
                  <Button variant="outline-secondary" disabled>
                    Import CSV
                  </Button>
                </span>
              </OverlayTrigger>
            </Col>
          </Row>
          <ListGroup>
            {filteredContacts.map((c) => {
              const checked = selectedContacts.some((s) => s.id === c.id);
              return (
                <ListGroup.Item
                  key={c.id}
                  className="d-flex align-items-center"
                >
                  <Form.Check
                    type="checkbox"
                    className="me-3"
                    checked={checked}
                    onChange={(e) => {
                      setSelectedContacts((prev) =>
                        e.target.checked
                          ? [...prev, c]
                          : prev.filter((p) => p.id !== c.id),
                      );
                    }}
                  />
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{c.name}</div>
                    <div className="small text-muted">
                      {c.email} · {c.phone}
                    </div>
                  </div>
                  <Badge bg="info">Suggested</Badge>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInviteSelected}
            disabled={selectedContacts.length === 0}
          >
            Invite{" "}
            {selectedContacts.length > 0 ? `(${selectedContacts.length})` : ""}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toasts */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={2500}
          autohide
          bg="dark"
        >
          <Toast.Body className="text-white">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Row>
  );
}
